import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Buildings, PencilSimple, Trash } from "@phosphor-icons/react";
import { updateRelatedEntity, deleteRelatedEntity } from "src/store/slices/profileMemorySlice";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, MODAL_NAMES, BUTTON_TYPES, BUTTON_ROLES, getPageName } from "src/utils/EventConstants";

const FIELDS = [
  { label: "Relationship", key: "relationship" },
  { label: "Location", key: "location" },
  { label: "Website", key: "website" },
  { label: "Details", key: "context", multiline: true },
];

const EntityCard = ({ entity }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.profileMemory.updateStatus === "loading");
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    setDraft({
      name: entity.name || "",
      relationship: entity.relationship || "",
      location: entity.location || "",
      website: entity.website || "",
      context: entity.context || "",
    });
  }, [entity]);

  const handleSave = () => {
    const changes = {};
    if (draft.name.trim() !== (entity.name || "")) changes.name = draft.name.trim();
    FIELDS.forEach(({ key }) => {
      if (draft[key]?.trim() !== (entity[key] || "")) changes[key] = draft[key].trim();
    });
    if (Object.keys(changes).length > 0) {
      dispatch(updateRelatedEntity({ entityId: entity.id, ...changes }));
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft({
      name: entity.name || "",
      relationship: entity.relationship || "",
      location: entity.location || "",
      website: entity.website || "",
      context: entity.context || "",
    });
    setEditing(false);
  };

  const memoryParams = {
    [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
    [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
    [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
    [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.MEMORY_SHELF,
    [EVENT_PARAMETERS.MEMORY_SECTION]: "people_and_companies",
    [EVENT_PARAMETERS.MEMORY_ITEM_ID]: entity.id,
  };

  const handleDelete = () => {
    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        ...memoryParams,
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
        [EVENT_PARAMETERS.BUTTON_TEXT]: "delete",
        [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.DELETE,
      },
      userMetadata,
    );
    if (window.confirm(`Remove "${entity.name || "this entity"}" from your memory?`)) {
      dispatch(deleteRelatedEntity({ entityId: entity.id }));
    }
  };

  if (editing) {
    return (
      <div className="border border-[#E4E4E4] rounded-xl bg-white p-3 flex flex-col gap-3">
        {/* Name field */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-[0.08em]">Name</label>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full text-[14px] text-[#1B2B48] bg-white border border-[#E4E4E4] rounded-[6px] px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#18475A]"
          />
        </div>
        {FIELDS.map((field) => (
          <div key={field.key} className="flex flex-col gap-1.5">
            <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-[0.08em]">
              {field.label}
            </label>
            {field.multiline ? (
              <textarea
                value={draft[field.key]}
                onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                className="w-full text-[14px] text-[#1B2B48] bg-white border border-[#E4E4E4] rounded-[6px] px-2 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#18475A]"
                rows={3}
              />
            ) : (
              <input
                type="text"
                value={draft[field.key]}
                onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                className="w-full text-[14px] text-[#1B2B48] bg-white border border-[#E4E4E4] rounded-[6px] px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#18475A]"
              />
            )}
          </div>
        ))}
        <div className="flex justify-end items-center gap-4 pt-2">
          <button
            onClick={handleCancel}
            className="text-[14px] font-medium text-[#6B7280] hover:text-[#1B2B48] cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-[14px] font-medium text-white bg-[#1B2B48] px-6 py-2 rounded-full hover:bg-[#0d1524] transition-colors cursor-pointer disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  const visibleFields = FIELDS.filter((f) => entity[f.key]);

  return (
    <div className="border border-[#E4E4E4] rounded-[6px] overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F0F0F0]">
        <div className="flex items-center gap-2">
          <Buildings size={16} weight="duotone" className="text-[#6B7280]" />
          <span className="text-[13px] font-semibold text-[#1B2B48]">{entity.name || "Unnamed Entity"}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              trackAnalytics(
                EVENT_NAMES.CLICKED_BUTTON,
                {
                  ...memoryParams,
                  [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
                  [EVENT_PARAMETERS.BUTTON_TEXT]: entity.type || "entity",
                  [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.EDIT,
                },
                userMetadata,
              );
              setEditing(true);
            }}
            className="p-1.5 rounded-full hover:bg-[#F4F4F6] transition-colors cursor-pointer"
          >
            <PencilSimple size={14} className="text-[#6B7280]" />
          </button>
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-full hover:bg-[#FEF2F2] transition-colors cursor-pointer"
          >
            <Trash size={14} className="text-[#6B7280]" />
          </button>
        </div>
      </div>

      {/* Fields */}
      <div className="py-1">
        {visibleFields.map((field) => (
          <div key={field.key} className="flex items-start px-4 py-2">
            <span className="text-[13px] text-[#6B7280] w-[120px] shrink-0">{field.label}</span>
            <span className={`text-[13px] text-[#1B2B48] flex-1 break-words${field.multiline ? " whitespace-pre-wrap" : ""}`}>{entity[field.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RelatedEntitySection = () => {
  const relatedEntities = useSelector((state) => state.profileMemory.relatedEntities);
  if (relatedEntities.length === 0) return
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-[18px] bg-[#1B2B48] rounded-full" />
          <h3 className="text-[13px] font-bold text-[#1B2B48] uppercase tracking-[0.05em]">People & Companies</h3>
        </div>
        <p className="text-[13px] text-[#9CA3AF] ml-[11px]">Clients, vendors, and others you've mentioned.</p>
      </div>

      {relatedEntities.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF] ml-[11px] italic">
          Clients, vendors, and others you mention in chat will show up here.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {relatedEntities.map((entity) => (
            <EntityCard key={entity.id} entity={entity} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RelatedEntitySection;
